import { FC, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Toolbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Menu } from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import MenuEditForms from "components/menu/ui/MenuEditForms";
import ColoredCircularProgress from "components/common/ui/ColorCircularProgress";
import { css } from "styled-components";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import foodItemOptionsSelector from "store/selectors/food/foodItemOptionsSelector";
import { actions as foodItemOptionsActions } from "store/slices/food/foodItemOptionsSlice";
import { menuDetailActions } from "store/slices/menu/menuDetailSlice";
import { rawMenuEditReadQuery } from "utils/menu/menu/menuQueryUtils";

interface CreateMenuInLoopDialogProps {
  open: boolean;
  onClose: () => void;
  initialName?: string;
  editingMenuId?: number | null;
  onSaved: (menu: Menu) => void;
}

const CreateMenuInLoopDialog: FC<CreateMenuInLoopDialogProps> = ({
  open,
  onClose,
  initialName,
  editingMenuId,
  onSaved,
}) => {
  const dispatch = useAppDispatch();
  const foodItemOptions = useAppSelector(
    foodItemOptionsSelector.foodDishOptions,
  );
  const [isLoadingMenu, setIsLoadingMenu] = useState(false);

  const isEditMode = !!editingMenuId;

  useEffect(() => {
    if (!open) {
      return;
    }
    dispatch(foodItemOptionsActions.fetchAllFoodItemOptions());
    if (editingMenuId) {
      setIsLoadingMenu(true);
      jspPlatformApi()
        // Load the raw stored menu for editing: no server-side fill-ins, so
        // the form only shows (and later saves) what the user actually set.
        .menu.getMenuMealByMenuId({
          path: { menu_id: editingMenuId },
          query: rawMenuEditReadQuery,
        })
        .then((res) => {
          dispatch(menuDetailActions.setMenuDetail(res.data ?? null));
        })
        .catch(() => {
          dispatch(menuDetailActions.setMenuDetail(null));
        })
        .finally(() => {
          setIsLoadingMenu(false);
        });
    } else {
      dispatch(menuDetailActions.reset());
    }
  }, [dispatch, editingMenuId, open]);

  useEffect(() => {
    return () => {
      dispatch(menuDetailActions.reset());
    };
  }, [dispatch]);

  const formKey = isEditMode
    ? `edit-${editingMenuId}`
    : `create-${initialName}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby="create-menu-in-loop-dialog-title"
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <DialogTitle id="create-menu-in-loop-dialog-title" sx={{ p: 0 }}>
          {isEditMode ? "Edit Menu Template" : "Create New Menu Template"}
        </DialogTitle>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <DialogContent>
        {open && isLoadingMenu ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <ColoredCircularProgress
              customColor={css`rgb(120, 203, 255)`}
              size={32}
            />
          </Box>
        ) : null}
        {open && !isLoadingMenu ? (
          <MenuEditForms
            key={formKey}
            foodItemOptions={foodItemOptions}
            initialName={isEditMode ? undefined : initialName}
            onSaveSuccess={onSaved}
            hideSaveAsNew
            hideDelete
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CreateMenuInLoopDialog;
